import { NavLink, Link } from 'react-router-dom';
import { useCategories } from '@/hooks/useProducts';
import { cn } from '@/lib/cn';
import { getSubcategoryLinks } from '@/lib/categoryMenu';
import type { Category } from '@/types/product';

// Curated fallback shown before the category tree loads (or if the API is down),
// so the nav never renders empty. Mirrors the active top-levels by sortOrder.
const FALLBACK: Pick<Category, 'name' | 'slug'>[] = [
  { name: 'Women', slug: 'women' },
  { name: 'Men', slug: 'men' },
  { name: 'Kids', slug: 'kids' },
  { name: 'Jewelry', slug: 'jewelry' },
  { name: 'Bags', slug: 'bags' },
  { name: 'Accessories', slug: 'accessories' },
];

/** Split a flat list into `cols` balanced columns, preserving order top-to-bottom. */
function intoColumns<T>(items: T[], cols: number): T[][] {
  const perCol = Math.ceil(items.length / cols);
  const columns: T[][] = [];
  for (let i = 0; i < items.length; i += perCol) {
    columns.push(items.slice(i, i + perCol));
  }
  return columns;
}

/**
 * Desktop primary nav (PRD §8.1) — data-driven from the live category tree.
 *
 * Each active top-level renders a magenta-underline NavLink. Top-levels with
 * active children also reveal a curated mega-menu on hover OR keyboard focus
 * (a `pt-3` bridge keeps the hover continuous across the gap, and
 * `group-focus-within` makes it operable without a mouse).
 *
 * The scraped taxonomy is noisy (empty names, glued "1k+ products" suffixes,
 * trailing chevrons, nav artefacts, and up to ~114 children on Jewelry), so the
 * panel sanitises labels and caps the list, then lays the result out in fixed-
 * width flex columns. Fixed-width columns are what kill the old overlap bug:
 * the previous `grid-cols-2` used `minmax(0,1fr)` tracks that collapsed under
 * `whitespace-nowrap`, stacking both columns of text on top of each other.
 */
export function DesktopNav() {
  const { data: categories } = useCategories();
  const tops = categories?.length ? categories : (FALLBACK as Category[]);

  return (
    <ul className="hidden items-center gap-1 md:flex">
      {tops.map((cat) => {
        // Apparel verticals use a hand-curated, search-backed menu; everything
        // else is data-driven from the live (scraped) category tree. Shared with
        // the filter sidebar so both stay in sync.
        const links = getSubcategoryLinks(cat);
        const hasChildren = links.length > 0;
        const to = `/category/${cat.slug}`;

        // Balance into 1–2 fixed-width columns so a centered panel never clips
        // the viewport edge under the right-most triggers. 5+ items split into
        // two columns so curated apparel menus read like the jewellery panel.
        const colCount = links.length >= 5 ? 2 : 1;
        const columns = intoColumns(links, colCount);

        return (
          <li key={cat.slug} className="group relative">
            <NavLink
              to={to}
              end={false}
              aria-haspopup={hasChildren || undefined}
              className={({ isActive }) =>
                cn(
                  'group/link relative inline-flex items-center px-4 py-2.5 text-[14px] font-medium tracking-wide transition-colors',
                  'text-[color:var(--color-text-secondary)] hover:text-white',
                  isActive && 'text-white'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span>{cat.name}</span>
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-x-3 -bottom-0.5 h-px origin-left scale-x-0 bg-[color:var(--color-brand-magenta)] transition-transform duration-200 ease-out group-hover:scale-x-100',
                      isActive && 'scale-x-100'
                    )}
                  />
                </>
              )}
            </NavLink>

            {hasChildren && (
              // pt-3 = invisible hover-bridge so the cursor can cross into the panel.
              <div
                className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 translate-y-1 pt-3 opacity-0 transition-[opacity,transform] duration-200 ease-out group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"
                role="menu"
                aria-label={`${cat.name} categories`}
              >
                <div
                  className="max-w-[min(92vw,720px)] overflow-hidden rounded-2xl border border-white/[0.08] shadow-[0_28px_60px_-16px_rgba(0,0,0,0.7)] backdrop-blur-xl"
                  style={{
                    background:
                      'linear-gradient(160deg, rgba(13,18,53,0.97) 0%, rgba(7,8,28,0.98) 100%)',
                  }}
                >
                  {/* Gold-mono eyebrow — labels the panel and adds a premium beat. */}
                  <div className="flex items-center gap-2 px-5 pt-4">
                    <span
                      className="text-[10px] font-semibold uppercase tracking-[0.22em]"
                      style={{ color: 'var(--color-gold)' }}
                    >
                      {cat.name}
                    </span>
                    <span className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
                  </div>

                  {/* Fixed-width flex columns — cannot overlap regardless of label length. */}
                  <div className="flex gap-x-6 px-5 py-3.5">
                    {columns.map((column, i) => (
                      <ul key={i} className="w-[184px] space-y-0.5">
                        {column.map((child) => (
                          <li key={child.to}>
                            <Link
                              to={child.to}
                              role="menuitem"
                              title={child.name}
                              className="group/item flex items-center justify-between gap-2 rounded-md px-3 py-2 text-[13px] text-[color:var(--color-text-secondary)] transition-colors hover:bg-white/[0.05] hover:text-white"
                            >
                              <span className="min-w-0 flex-1 truncate">{child.name}</span>
                              <span
                                aria-hidden
                                className="shrink-0 translate-x-1 text-[color:var(--color-brand-magenta-light)] opacity-0 transition-all duration-200 group-hover/item:translate-x-0 group-hover/item:opacity-100"
                              >
                                →
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>

                  <Link
                    to={to}
                    role="menuitem"
                    className="flex items-center gap-1.5 border-t border-white/[0.06] px-5 py-3 text-[12px] font-medium tracking-wide text-[color:var(--color-brand-magenta-light)] transition-colors hover:text-white"
                  >
                    Shop all {cat.name}
                    <span aria-hidden>→</span>
                  </Link>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
